import { SceneService } from '@src/services/SceneService';

import { Scene } from '../models/layout';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { IScene } from '../models/layout/types';
import type { VideoEditor } from '../VideoEditor';

type SceneUpdatePayload = {
  sceneId: string;
  updates: Partial<IScene>;
};

export class UpdateScenesCommand extends AbstractCommand {
  private readonly updates: SceneUpdatePayload[];
  private readonly previousScenes: Map<string, Scene> = new Map();
  private readonly service = new SceneService();

  constructor(editor: VideoEditor, updates: SceneUpdatePayload[]) {
    super(editor);
    this.updates = updates;
  }

  getName(): string {
    return 'UpdateScenesCommand';
  }

  execute(): boolean {
    console.log('Execute UpdateScenesCommand', this.updates);
    if (!this.updates.length) {
      return true;
    }

    const appliedScenes: string[] = [];

    for (const payload of this.updates) {
      const existing = this.service.getScene(
        this.editor.timeline,
        payload.sceneId,
      );

      if (!existing) {
        this.restoreAppliedScenes(appliedScenes);
        return false;
      }

      this.previousScenes.set(
        payload.sceneId,
        Scene.fromJSON({ ...existing.toJSON(), id: payload.sceneId }),
      );

      const updatedScene = Scene.fromJSON({
        ...existing.toJSON(),
        ...payload.updates,
        id: payload.sceneId,
      });

      const updated = this.service.updateScene(
        this.editor.timeline,
        payload.sceneId,
        updatedScene,
      );

      if (!updated) {
        this.restoreAppliedScenes(appliedScenes);
        return false;
      }

      appliedScenes.push(payload.sceneId);

      this.editor.notifyObservers(EditorChangeType.SCENE_UPDATED, {
        sceneId: payload.sceneId,
        updates: payload.updates,
      });
    }

    this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);

    this.editor.markDirty('command:update-scenes');
    return true;
  }

  undo(): boolean {
    if (!this.previousScenes.size) {
      return true;
    }

    for (const payload of [...this.updates].reverse()) {
      const snapshot = this.previousScenes.get(payload.sceneId);
      if (!snapshot) {
        continue;
      }

      const restored = this.service.updateScene(
        this.editor.timeline,
        payload.sceneId,
        snapshot,
      );

      if (!restored) {
        return false;
      }

      this.editor.notifyObservers(EditorChangeType.SCENE_UPDATED, {
        sceneId: payload.sceneId,
        updates: snapshot.toJSON(),
      });
    }

    this.editor.markDirty('command:update-scenes:undo');
    return true;
  }

  redo(): boolean {
    return this.execute();
  }

  getDescription(): string {
    return `Update ${this.updates.length} scene(s)`;
  }

  private restoreAppliedScenes(appliedSceneIds: string[]): void {
    if (!appliedSceneIds.length) {
      return;
    }

    for (const sceneId of appliedSceneIds.reverse()) {
      const snapshot = this.previousScenes.get(sceneId);
      if (!snapshot) {
        continue;
      }

      this.service.updateScene(this.editor.timeline, sceneId, snapshot);
    }
  }
}
